import { commandSchema, newId, type Snapshot, type Command, type Venture } from './models';
import { activeProjectCap } from './plans';
import { canAccessCollaborator, isAI } from './reputation';
export function dmChannel(a: string, b: string) {
  return `dm:${[a, b].sort().join(':')}`;
}
export function applyCommand(
  source: Snapshot,
  input: Command,
  actor: string,
  now = new Date().toISOString(),
): Snapshot {
  const c = commandSchema.parse(input);
  const s = JSON.parse(JSON.stringify(source)) as Snapshot;
  const user = s.profiles.find((p) => p.id === actor);
  if (!user) throw Error('Founder not found.');
  const bot = (id: string) => id === 'ai-assistant' || !!s.profiles.find((p) => p.id === id)?.is_ai;
  if (bot(actor) && !['message', 'connectionRespond', 'invitationRespond'].includes(c.type))
    throw Error('AI cannot initiate autonomous social actions.');
  const person = (id: string) => {
    if (!s.profiles.some((p) => p.id === id)) throw Error('Founder not found.');
  };
  const venture = (id: string) => {
    const v = s.ventures.find((v) => v.id === id);
    if (!v) throw Error('Venture not found.');
    return v;
  };
  const member = (id: string) => {
    const v = venture(id);
    if (!v.members.includes(actor) && actor !== 'venture-bot')
      throw Error('Join this venture before making changes.');
    return v;
  };
  const access = (id: string) => {
    const target = s.profiles.find((p) => p.id === id);
    if (target && !canAccessCollaborator(s, user, target))
      throw Error(
        'Your plan does not include this collaborator rating. View plans to unlock higher-rated collaborators.',
      );
  };
  const connected = (id: string) =>
    s.connections.some(
      (r) =>
        r.status === 'accepted' && [r.from, r.to].includes(actor) && [r.from, r.to].includes(id),
    );
  const members = (v: Venture, ids: string[]) => {
    if (ids.some((id) => !v.members.includes(id))) throw Error('Choose venture team members only.');
  };
  const ensureProjectRoom = (id: string) => {
    const p = s.profiles.find((x) => x.id === id);
    if (!p) return;
    const active = s.ventures.filter((v) => v.members.includes(id) && v.stage !== 'Closed').length;
    if (active >= activeProjectCap(p))
      throw Error(
        id === actor
          ? 'Your plan limits how many active projects you can be part of. Upgrade your plan to join more.'
          : "This person has reached their plan's active project limit.",
      );
  };
  const channel = (id: string) => {
    if (id.startsWith('dm:')) {
      const pair = id.slice(3).split(':');
      if (pair.every(bot)) throw Error('Autonomous AI-to-AI messages are not allowed.');
      if (
        pair.length !== 2 ||
        !pair.includes(actor) ||
        !connected(pair.find((p) => p !== actor) ?? actor)
      )
        throw Error('Connect before starting a conversation.');
    } else if (id.startsWith('venturebot-')) {
      member(id.replace('venturebot-', ''));
    } else if (id.startsWith('boardroom-')) {
      member(id.replace('boardroom-', ''));
    } else if (id.startsWith('team:')) {
      const owner = s.profiles.find((p) => p.id === id.slice(5));
      if (!owner || (owner.id !== actor && !owner.team?.includes(actor)))
        throw Error('Team membership required.');
    } else {
      member(id);
    }
  };
  const activity = (
    title: string,
    kind: Snapshot['activities'][number]['kind'],
    ventureId?: string,
  ) =>
    s.activities.unshift({
      id: newId('activity'),
      title,
      detail: 'Recorded on platform',
      kind,
      ventureId,
      createdAt: now,
    });
  const unique = (items: { id: string }[], id: string) => {
    if (items.some((i) => i.id === id)) throw Error('This record already exists.');
  };
  switch (c.type) {
    case 'buyVentureUpgrade': {
      const v = member(c.ventureId);
      if (!v.upgrades) v.upgrades = [];
      if (!v.upgrades.includes(c.upgrade)) v.upgrades.push(c.upgrade);
      break;
    }
    case 'topUpCredits': {
      const v = s.ventures.find((v) => v.id === c.ventureId);
      if (!v) throw Error('Venture not found');
      if (!v.credits) v.credits = { balance: 1000, purchaserId: actor, isShared: c.isShared };

      // Convert amount ($) to words. Assume $4.80 buys 150,000 words. So 1 dollar = 31250 words.
      const words = Math.floor(c.amount * 0.8 * 31250);
      v.credits.balance += words;
      v.credits.purchaserId = actor;
      v.credits.isShared = c.isShared;
      break;
    }
    case 'updateWebsiteStats': {
      const v = s.ventures.find((v) => v.id === c.ventureId);
      if (!v) throw Error('Venture not found');
      v.websiteStatsConfig = { endpoint: c.endpoint, apiKey: c.apiKey };
      break;
    }
    case 'trackWebsiteHit': {
      const v = member(c.ventureId);
      if (!v.dailyStats) v.dailyStats = {};
      const stats = (v.dailyStats[c.date] ??= {
        views: 0,
        visitors: 0,
        checkouts: 0,
        sales: 0,
        revenue: 0,
      });

      if (!c.event || c.event === 'pageview') {
        stats.views += 1;
        if (c.unique) stats.visitors += 1;
      } else if (c.event === 'checkout') {
        stats.checkouts += 1;
      } else if (c.event === 'sale') {
        stats.sales += 1;
        stats.revenue += c.value || 0;
      }
      break;
    }

    case 'profile': {
      if (
        c.profile.investorType &&
        c.profile.investorType !== 'None' &&
        !(c.profile.investmentBudgetMinor && c.profile.investmentBudgetMinor > 0)
      )
        throw Error(
          'Investors must declare an indicative budget. This is not a commitment to invest.',
        );
      if (
        c.profile.is_ai !== user.is_ai ||
        JSON.stringify(c.profile.ai) !== JSON.stringify(user.ai)
      )
        throw Error('Identity type cannot be changed.');
      if (c.profile.id !== actor) throw Error('You can only edit your own profile.');
      const existing = s.profiles.find((p) => p.id === actor);
      s.profiles = s.profiles.map((p) =>
        p.id === actor
          ? {
              ...c.profile,
              username: existing?.username,
              subscriptionTier: existing?.subscriptionTier,
              reputation: existing?.reputation,
              ventureSummaries: existing?.ventureSummaries,
              team: existing?.team,
              stats: existing?.stats,
            }
          : p,
      );
      break;
    }
    case 'bookmark':
      person(c.personId);
      if (c.personId === actor) throw Error('Choose another founder.');
      s.bookmarks = s.bookmarks.filter((b) => !(b.userId === actor && b.personId === c.personId));
      if (c.state !== 'remove')
        s.bookmarks.push({
          id: `${actor}-${c.personId}`,
          userId: actor,
          personId: c.personId,
          state: c.state,
        });
      if (c.state === 'passed') {
        for (const r of s.connections) {
          if (
            r.status === 'pending' &&
            [r.from, r.to].includes(actor) &&
            [r.from, r.to].includes(c.personId)
          )
            r.status = 'declined';
        }
      }
      break;
    case 'connect': {
      access(c.personId);
      person(c.personId);
      if (c.personId === actor) throw Error('Choose another founder.');
      const existing = s.connections.find(
        (r) =>
          r.status !== 'declined' &&
          [r.from, r.to].includes(actor) &&
          [r.from, r.to].includes(c.personId),
      );
      s.bookmarks = s.bookmarks.filter(
        (b) => !(b.userId === actor && b.personId === c.personId && b.state === 'passed'),
      );
      if (existing) {
        if (existing.status === 'pending' && existing.to === actor) {
          existing.status = 'accepted';
          activity(`${user?.name.split(' ')[0]} made a mutual match`, 'connection');
        }
        // Retrying a like must never create another connection or imply mutual consent.
        break;
      }
      s.connections.push({
        id: newId('connection'),
        from: actor,
        to: c.personId,
        intro: c.intro,
        status: 'pending',
        createdAt: now,
      });
      activity(`${user?.name.split(' ')[0]} liked a founder`, 'connection');
      break;
    }
    case 'connectionArchive': {
      const connection = s.connections.find((r) => r.id === c.id && [r.from, r.to].includes(actor));
      if (!connection) throw Error('Connection not found.');
      connection.status = 'declined';
      break;
    }
    case 'connectionRespond': {
      const r = s.connections.find((r) => r.id === c.id);
      if (!r || r.to !== actor || r.status !== 'pending')
        throw Error('This request is no longer available.');
      if (c.accept && !bot(actor)) access(r.from);
      r.status = c.accept ? 'accepted' : 'declined';
      activity(c.accept ? 'A new connection begins' : 'Connection request declined', 'connection');
      break;
    }
    case 'subscribe': {
      if (!user) throw Error('Founder not found.');
      user.subscriptionTier = c.tier;
      break;
    }
    case 'teamMember': {
      if (user.role === 'Business Owner' || user.subscriptionTier !== 'agency')
        throw Error('Building a team is part of the Agency plan.');
      const team: string[] = user.team ?? [];
      if (c.action === 'remove') {
        user.team = team.filter((id: string) => id !== c.personId);
        break;
      }
      person(c.personId);
      if (c.personId === actor) throw Error('You are already the head of your team.');
      const target = s.profiles.find((p) => p.id === c.personId)!;
      if (target.role === 'Business Owner') throw Error('Only skill providers can join your team.');
      if (!connected(c.personId)) throw Error('Add an accepted connection to your team.');
      if (team.includes(c.personId)) throw Error('This person is already on your team.');
      if (team.length >= 20) throw Error('Your team is full.');
      user.team = [...team, c.personId];
      break;
    }
    case 'track': {
      person(c.personId);
      if (c.personId === actor) break;
      const target = s.profiles.find((p) => p.id === c.personId)!;
      const stats = target.stats ?? { profileViews: 0, searchAppearances: 0 };
      if (c.kind === 'profileView') stats.profileViews += 1;
      else stats.searchAppearances += 1;
      target.stats = stats;
      break;
    }
    case 'message':
      channel(c.channel);
      if (c.replyTo && !s.messages.some((m) => m.id === c.replyTo && m.channel === c.channel))
        throw Error('Reply must belong to this conversation.');
      s.messages.push({
        id: newId('message'),
        channel: c.channel,
        author: actor,
        text: c.text,
        imageUrl: c.imageUrl,
        fileUrl: c.fileUrl,
        fileName: c.fileName,
        websiteCode: c.websiteCode,
        artifactId: c.artifactId,
        replyTo: c.replyTo,
        reference: c.reference,
        reactions: [],
        createdAt: now,
      });
      break;
    case 'react': {
      const m = s.messages.find((m) => m.id === c.id);
      if (!m) throw Error('Message not found.');
      channel(m.channel);
      m.reactions = m.reactions.includes(actor)
        ? m.reactions.filter((id) => id !== actor)
        : [...m.reactions, actor];
      break;
    }
    case 'venture': {
      if (user.role === 'Skill Provider')
        throw Error(
          'Skill providers join ventures started by business owners. You cannot start one.',
        );
      ensureProjectRoom(actor);
      if (!c.venture.name.trim() || !c.venture.description.trim())
        throw Error('Complete the venture name and public venture description.');
      unique(s.ventures, c.venture.id);
      const invitees = c.venture.members.filter((id) => id !== actor);
      for (const id of invitees) access(id);
      if (invitees.some((id) => !connected(id))) throw Error('Invite accepted connections only.');
      s.ventures.push({
        ...c.venture,
        premiumWork: undefined,
        memberSummaries: undefined,
        members: [actor],
        createdAt: now,
        credits: { balance: 10000, purchaserId: actor, isShared: true },
        upgrades: [],
      });
      for (const to of invitees) {
        const invitationId = newId('invite');
        s.invitations.push({
          id: invitationId,
          ventureId: c.venture.id,
          from: actor,
          to,
          kind: 'invitation',
          status: 'pending',
          note: '',
          createdAt: now,
        });
        s.messages.push({
          id: newId('message'),
          channel: dmChannel(actor, to),
          author: actor,
          text: `I’d like to invite you to ${c.venture.name}.`,
          invitationId,
          reactions: [],
          createdAt: now,
        });
      }
      activity(`${c.venture.name} is taking shape`, 'venture', c.venture.id);
      break;
    }
    case 'stage': {
      const v = member(c.ventureId);
      if (v.members[0] !== actor) throw Error('Only the venture owner can edit its settings.');
      if (v.stage === 'Closed' && c.stage !== 'Closed')
        for (const id of v.members) ensureProjectRoom(id);
      v.stage = c.stage;
      activity(`Venture moved to ${c.stage}`, 'venture', c.ventureId);
      break;
    }
    case 'ventureUpdate': {
      const v = member(c.ventureId);
      if (v.members[0] !== actor) throw Error('Only the venture owner can edit its settings.');
      if (v.stage === 'Closed' && c.changes.stage !== 'Closed')
        for (const id of v.members) ensureProjectRoom(id);
      if (!c.changes.name.trim() || !c.changes.description.trim())
        throw Error('Complete the venture name and public venture description.');
      Object.assign(v, c.changes);
      activity('Venture settings updated', 'venture', v.id);
      break;
    }
    case 'ventureLogo': {
      const v = member(c.ventureId);
      if (v.members[0] !== actor) throw Error('Only the venture owner can change its logo.');
      v.logo = c.logo;
      break;
    }
    case 'invite': {
      access(c.personId);
      person(c.personId);
      const v = member(c.ventureId);
      if (v.members[0] !== actor) throw Error('Only the venture owner can invite teammates.');
      if (v.members.includes(c.personId)) throw Error('This person is already on the team.');
      if (
        s.invitations.some(
          (i) => i.ventureId === v.id && i.to === c.personId && i.status === 'pending',
        )
      )
        throw Error('An invitation is already pending.');
      const invitationId = newId('invite');
      s.invitations.push({
        id: invitationId,
        ventureId: v.id,
        from: actor,
        to: c.personId,
        kind: 'invitation',
        status: 'pending',
        note: '',
        createdAt: now,
      });
      s.messages.push({
        id: newId('message'),
        channel: dmChannel(actor, c.personId),
        author: actor,
        text: `I’d like to invite you to ${v.name}.`,
        invitationId,
        reactions: [],
        createdAt: now,
      });
      activity('A teammate was invited', 'venture', v.id);
      break;
    }
    case 'join': {
      const v = venture(c.ventureId);
      if (v.members.includes(actor)) throw Error('You are already on this team.');
      if (v.stage === 'Closed') throw Error('This venture is closed.');
      for (const id of v.members) access(id);
      ensureProjectRoom(actor);
      if (
        s.invitations.some(
          (i) =>
            i.ventureId === v.id &&
            i.from === actor &&
            i.kind === 'request' &&
            i.status === 'pending',
        )
      )
        throw Error('Your collaboration request is already pending.');
      s.invitations.push({
        id: newId('request'),
        ventureId: v.id,
        from: actor,
        to: v.members[0]!,
        kind: 'request',
        status: 'pending',
        note: c.note,
        createdAt: now,
      });
      break;
    }
    case 'invitationCancel': {
      const invitation = s.invitations.find((i) => i.id === c.id);
      if (
        !invitation ||
        invitation.kind !== 'invitation' ||
        invitation.status !== 'pending' ||
        invitation.from !== actor
      )
        throw Error('This pending invitation is not available.');
      const v = member(invitation.ventureId);
      if (v.members[0] !== actor) throw Error('Only the venture owner can cancel invitations.');
      invitation.status = 'cancelled';
      activity('Venture invitation cancelled', 'venture', v.id);
      break;
    }
    case 'invitationRespond': {
      const i = s.invitations.find((i) => i.id === c.id);
      if (!i || i.status !== 'pending' || i.to !== actor)
        throw Error('This invitation is not available.');
      const v = venture(i.ventureId);
      if (i.kind === 'request' && v.members[0] !== actor)
        throw Error('Only the venture owner can review requests.');
      const joining = i.kind === 'request' ? i.from : i.to;
      if (c.accept && !bot(joining)) {
        const participant = s.profiles.find((p) => p.id === joining)!;
        if (
          v.members.some((id) => {
            const target = s.profiles.find((p) => p.id === id);
            return target && !canAccessCollaborator(s, participant, target);
          })
        )
          throw Error('Your plan does not include a collaborator rating on this venture.');
      }
      if (c.accept && !v.members.includes(joining)) ensureProjectRoom(joining);
      i.status = c.accept ? 'accepted' : 'declined';
      if (c.accept && !v.members.includes(joining)) {
        if (v.members.length >= 20) throw Error('This venture has reached its team limit.');
        v.members.push(joining);
        const other = i.kind === 'request' ? actor : i.from;
        const connection = s.connections.find(
          (r) => [r.from, r.to].includes(joining) && [r.from, r.to].includes(other),
        );
        if (connection) connection.status = 'accepted';
        else
          s.connections.push({
            id: newId('connection'),
            from: other,
            to: joining,
            status: 'accepted',
            intro: i.note ?? '',
            createdAt: now,
          });
      }
      activity(
        c.accept ? 'A teammate joined the venture' : 'Venture invitation declined',
        'venture',
        v.id,
      );
      break;
    }
    case 'decision': {
      const v = member(c.decision.ventureId);
      unique(s.decisions, c.decision.id);
      members(v, c.decision.participants);
      if (c.decision.deadline <= now) throw Error('Choose a future decision deadline.');
      s.decisions.push({
        ...c.decision,
        author: actor,
        votes: {},
        comments: [],
        closedAt: undefined,
        createdAt: now,
      });
      activity(c.decision.title, 'decision', v.id);
      break;
    }
    case 'vote': {
      const d = s.decisions.find((d) => d.id === c.id);
      if (!d) throw Error('Decision not found.');
      member(d.ventureId);
      if (!d.participants.includes(actor))
        throw Error('You are not a participant in this decision.');
      if (d.closedAt || d.deadline <= now) throw Error('Voting has closed.');
      if (c.option >= d.options.length) throw Error('Choose a listed option.');
      d.votes[actor] = c.option;
      break;
    }
    case 'closeDecision': {
      const d = s.decisions.find((d) => d.id === c.id);
      if (!d) throw Error('Decision not found.');
      member(d.ventureId);
      if (d.author !== actor) throw Error('Only the decision author can close it early.');
      if (d.closedAt) throw Error('This decision is already closed.');
      d.closedAt = now;
      activity(`Decision closed: ${d.title}`, 'decision', d.ventureId);
      break;
    }
    case 'decisionComment': {
      const d = s.decisions.find((d) => d.id === c.id);
      if (!d) throw Error('Decision not found.');
      member(d.ventureId);
      d.comments.push({ author: actor, text: c.text });
      break;
    }
    case 'commitment': {
      const v = member(c.commitment.ventureId);
      unique(s.commitments, c.commitment.id);
      members(v, [c.commitment.owner]);
      s.commitments.push({
        ...c.commitment,
        comments: [],
        completedAt: c.commitment.status === 'Completed' ? now : undefined,
        createdAt: now,
      });
      activity(`New commitment: ${c.commitment.title}`, 'commitment', v.id);
      break;
    }
    case 'commitmentStatus': {
      const t = s.commitments.find((t) => t.id === c.id);
      if (!t) throw Error('Commitment not found.');
      member(t.ventureId);
      t.status = c.status;
      t.completedAt = c.status === 'Completed' ? (t.completedAt ?? now) : undefined;
      activity(`${t.title} · ${c.status}`, 'commitment', t.ventureId);
      break;
    }
    case 'commitmentComment': {
      const t = s.commitments.find((t) => t.id === c.id);
      if (!t) throw Error('Commitment not found.');
      member(t.ventureId);
      t.comments.push({ author: actor, text: c.text });
      break;
    }
    case 'event': {
      const v = member(c.event.ventureId);
      unique(s.events, c.event.id);
      members(v, c.event.participants);
      s.events.push(c.event);
      activity(c.event.title, 'meeting', v.id);
      break;
    }
    case 'contribution': {
      const l = c.contribution;
      member(l.ventureId);
      unique(s.contributions, l.id);
      if (l.contributor !== actor) throw Error('Record your own contributions.');
      if (l.date > now.slice(0, 10))
        throw Error('Historical contributions cannot have a future date.');
      if (l.type !== 'Expense paid' && l.expenseMinor !== undefined)
        throw Error('Amounts belong only to historical expenses.');
      if (
        l.commitmentId &&
        !s.commitments.some((t) => t.id === l.commitmentId && t.ventureId === l.ventureId)
      )
        throw Error('Link a commitment from this venture.');
      s.contributions.push({ ...l, votes: {}, createdAt: now });
      activity(l.title, 'contribution', l.ventureId);
      break;
    }
    case 'contributionVote': {
      const entry = s.contributions.find((x) => x.id === c.id);
      if (!entry) throw Error('Contribution not found.');
      member(entry.ventureId);
      const target = s.profiles.find((p) => p.id === entry.contributor);
      if (isAI(user) || !target || isAI(target))
        throw Error('Only humans can vote on human contributions. AI ratings are fixed.');
      if (entry.contributor === actor) throw Error('You cannot vote on your own contribution.');
      entry.votes ??= {};
      if (c.value === 0) delete entry.votes[actor];
      else entry.votes[actor] = c.value;
      break;
    }
    case 'endorse': {
      const e = c.endorsement;
      const v = member(e.ventureId);
      members(v, [e.to]);
      if (e.to === actor) throw Error('Endorse a teammate.');
      if (
        s.endorsements.some(
          (x) =>
            x.from === actor && x.to === e.to && x.skill === e.skill && x.ventureId === e.ventureId,
        )
      )
        throw Error('You have already recorded this endorsement.');
      s.endorsements.push({ ...e, from: actor });
      break;
    }
  }
  return s;
}
